import React, { useState } from 'react'
import { Alert, Box, Button, Chip, Collapse, Grid, LinearProgress, Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { formatCount, formatRate, formatSourceLabel, formatUpdatedAt, WARNING_LABELS, WARNING_UNITS } from './membershipFunnelUtils'

const COLORS = ['#2385d7', '#19a778', '#7e78ce', '#a2acb8', '#d5a044']
const SEGMENT_COLORS = { new_member: '#2385d7', new_or_rejoined: '#2385d7', rejoined_member: '#19a778', existing_member: '#7e78ce', non_member: '#a2acb8', unknown: '#d5a044' }
const distributionColor = (item, index) => SEGMENT_COLORS[item.segment] || COLORS[index % COLORS.length]
export const panelSx = { p: { xs: 2, md: 2.5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 18px rgba(25,32,38,0.03)' }
const numberSx = { fontVariantNumeric: 'tabular-nums', fontWeight: 700 }

export function SectionTitle({ children, note }) {
    return <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={0.5} sx={{ mb: 1.5 }}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>{children}</Typography>
        {note && <Typography variant="caption" color="text.secondary" sx={{ alignSelf: { md: 'center' } }}>{note}</Typography>}
    </Stack>
}

export function ResourceState({ resource, label }) {
    if (resource.loading) return <Paper variant="outlined" sx={panelSx} role="status"><Typography sx={{ mb: 1 }}>{label} 불러오는 중…</Typography><LinearProgress /></Paper>
    if (resource.error) return <Alert severity="warning">{resource.error}</Alert>
    return null
}

export function DataMetadata({ data, dateLabel }) {
    return <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 0.75 }}>
            <Chip size="small" label={data.meta.dataStatus === 'final' ? '확정' : '잠정 포함'} color={data.meta.dataStatus === 'final' ? 'success' : 'warning'} variant="outlined" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>적용 기간: {data.filter.from} ~ {data.filter.to}</Typography>
            <Typography variant="caption" color="text.secondary">{dateLabel} · KST</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" component="p">
            제공 범위: {data.meta.availableFrom} ~ {data.meta.dataThrough} · 확정 기준일: {data.meta.finalizedThrough || '아직 없음'} · 갱신: {formatUpdatedAt(data.meta.refreshedAt)} (KST)
        </Typography>
        {data.meta.dataStatus === 'provisional' && <Typography variant="caption" color="text.secondary" component="p">잠정 구간은 후속 전환과 재집계에 따라 값이 달라질 수 있습니다.</Typography>}
        {data.meta.qualityWarnings?.length > 0 && <Alert severity="warning" sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>데이터 품질 안내</Typography>
            {data.meta.qualityWarnings.map((warning, index) => <Typography variant="body2" key={`${warning.code}-${index}`}>
                {WARNING_LABELS[warning.code] || warning.code}: {formatCount(warning.count)}{WARNING_UNITS[warning.unit] || warning.unit}
            </Typography>)}
            <Typography variant="caption">경고는 중복될 수 있으며 합계를 이탈 사용자 수로 해석하지 않습니다.</Typography>
        </Alert>}
    </Box>
}

function Metric({ label, value, note, accent }) {
    return <Paper variant="outlined" sx={{ ...panelSx, height: '100%', bgcolor: accent ? '#eef7ff' : 'background.paper' }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography sx={{ ...numberSx, fontSize: value === '해당 없음' ? 22 : 30, mt: 0.75, color: accent ? '#155f9f' : 'text.primary' }}>{value}</Typography>
        {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
    </Paper>
}

function Distribution({ items, countKey, unit, label }) {
    return <Box>
        <Box role="img" aria-label={`${label}: ${items.map(item => `${item.label} ${formatRate(item.rate)}`).join(', ')}`} sx={{ display: 'flex', height: 20, borderRadius: 1, overflow: 'hidden', bgcolor: '#eef1f4', mb: 2 }}>
            {items.map((item, index) => <Box key={item.segment || item.plan} sx={{ width: `${(item.rate || 0) * 100}%`, bgcolor: distributionColor(item, index) }} />)}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 1.5 }}>
            {items.map((item, index) => <Box key={item.segment || item.plan}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: distributionColor(item, index) }} />
                    <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                </Stack>
                <Typography sx={{ ...numberSx, fontSize: 22, mt: 0.5 }}>{formatCount(item[countKey])}{unit}</Typography>
                <Typography variant="body2" color="text.secondary">{formatRate(item.rate)}</Typography>
            </Box>)}
        </Box>
    </Box>
}

function SourceRow({ source }) {
    const [expanded, setExpanded] = useState(false)
    const detailsId = `membership-source-${source.entrySource}`
    return <>
        <TableRow hover>
            <TableCell>
                <Button color="inherit" onClick={() => setExpanded(value => !value)} aria-expanded={expanded} aria-controls={detailsId}
                    aria-label={`${formatSourceLabel(source)} 단계 ${expanded ? '접기' : '펼치기'}`}
                    startIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />}
                    sx={{ textAlign: 'left', justifyContent: 'flex-start', fontWeight: 700, minWidth: 175 }}>
                    {formatSourceLabel(source)}
                </Button>
            </TableCell>
            <TableCell sx={{ maxWidth: 220, overflowWrap: 'anywhere', color: 'text.secondary', fontSize: 11 }}>
                {source.promotionIds.map((id, index) => <Box key={id || index}>{id || '해당 없음'}</Box>)}
            </TableCell>
            {['impressionCount', 'clickCount', 'pageViewCount', 'purchaseCount'].map(key => <TableCell key={key} align="right" sx={{ ...numberSx, whiteSpace: 'nowrap' }}>{formatCount(source[key])}</TableCell>)}
            <TableCell align="right" sx={numberSx}>{source.impressionCount === null ? '해당 없음' : formatRate(source.impressionToPurchaseRate)}</TableCell>
            <TableCell align="right" sx={numberSx}>{formatRate(source.entryToPurchaseRate)}</TableCell>
        </TableRow>
        <TableRow>
            <TableCell colSpan={8} sx={{ py: 0, borderBottom: expanded ? undefined : 0, bgcolor: '#f8fafb' }}>
                <Collapse in={expanded} unmountOnExit>
                    <Box id={detailsId} sx={{ py: 2 }}>
                        <Stack direction="row" flexWrap="wrap" gap={1.5}>
                            {source.steps.map((step, index) => <Paper key={step.stage} variant="outlined" sx={{ px: 2, py: 1.5, flex: '1 1 140px' }}>
                                <Typography variant="body2" color="text.secondary">{index + 1}. {step.label}</Typography>
                                <Typography sx={{ ...numberSx, fontSize: 22 }}>{formatCount(step.count)}</Typography>
                                <Typography variant="caption" color="text.secondary">{index === 0 ? '진입 시작 단계' : `직전 단계 대비 ${formatRate(step.conversionFromPreviousRate)}`}</Typography>
                            </Paper>)}
                        </Stack>
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow>
    </>
}

function DailyTrend({ data, impressionSupported }) {
    const [showTable, setShowTable] = useState(false)
    const rateKey = impressionSupported ? 'impressionToPurchaseRate' : 'entryToPurchaseRate'
    const chartTitle = impressionSupported ? '일별 노출 대비 가입 완료율' : '일별 진입 대비 가입 완료율'
    const points = data.days.map(day => ({ ...day, ratePercent: day[rateKey] == null ? null : day[rateKey] * 100 }))
    const hasRate = points.some(day => day.ratePercent !== null)
    return <Box>
        <SectionTitle note="진입 시작일 기준 · 진입 후 7일 이내">{chartTitle}</SectionTitle>
        <Paper sx={panelSx}>
            {!impressionSupported && <Alert severity="info" sx={{ mb: 2 }}>노출 미수집 경로입니다. 시작 단계 대비 가입 완료율을 표시합니다.</Alert>}
            {impressionSupported && <Typography variant="caption" color="text.secondary">노출 수집 진입점 기준 · 단위: %</Typography>}
            {hasRate ? <Box sx={{ height: 280, mt: 1, minWidth: 0 }} role="img" aria-label={chartTitle}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={points} margin={{ top: 20, right: 24, bottom: 4, left: 0 }}>
                        <CartesianGrid stroke="#e8edf1" strokeDasharray="4 4" vertical={false} />
                        <XAxis dataKey="date" tickFormatter={date => date.slice(5)} minTickGap={32} tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={value => `${value}%`} tick={{ fontSize: 12 }} domain={[0, 'auto']} />
                        <Tooltip formatter={value => [formatRate(value / 100), '가입 완료율']} labelFormatter={(date, payload) => `${date} · ${payload?.[0]?.payload?.dataStatus === 'final' ? '확정' : '잠정'}`} />
                        <Line dataKey="ratePercent" name="가입 완료율" stroke="#2385d7" strokeWidth={3} dot={points.length <= 31} activeDot={{ r: 5 }} connectNulls={false} isAnimationActive={false} />
                    </LineChart>
                </ResponsiveContainer>
            </Box> : <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>전환율을 계산할 수 있는 진입 데이터가 없습니다.</Box>}
            <Button onClick={() => setShowTable(value => !value)} aria-expanded={showTable} aria-controls="membership-daily-table">일별 데이터 {showTable ? '접기' : '보기'}</Button>
            <Collapse in={showTable} unmountOnExit>
                <TableContainer id="membership-daily-table">
                    <Table size="small" aria-label="멤버십 일별 전환 데이터">
                        <TableHead><TableRow>{['진입 시작일', '상태', '진입', '노출', '클릭', '랜딩 진입', '가입 완료', '전환율'].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
                        <TableBody>{data.days.map(day => <TableRow key={day.date}>
                            <TableCell>{day.date}</TableCell><TableCell>{day.dataStatus === 'final' ? '확정' : '잠정'}</TableCell>
                            {['entryCount', 'impressionCount', 'clickCount', 'pageViewCount', 'purchaseCount'].map(key => <TableCell key={key}>{formatCount(day[key])}</TableCell>)}
                            <TableCell>{formatRate(day[rateKey])}</TableCell>
                        </TableRow>)}</TableBody>
                    </Table>
                </TableContainer>
            </Collapse>
        </Paper>
    </Box>
}

export function FunnelSummary({ data }) {
    const { kpis, landing } = data
    const impressionSupported = kpis.impressionCount !== null
    const metrics = [
        ['노출', formatCount(kpis.impressionCount)], ['클릭', formatCount(kpis.clickCount)],
        ['랜딩 진입', formatCount(kpis.pageViewCount)], ['가입 완료', formatCount(kpis.purchaseCount)],
        [impressionSupported ? '노출 대비 전환율' : '진입 대비 전환율', formatRate(impressionSupported ? kpis.impressionToPurchaseRate : kpis.entryToPurchaseRate), impressionSupported ? '노출 수집 진입점 기준' : '시작 단계 기준'],
    ]
    return <Stack spacing={3.5}>
        <Box>
            <SectionTitle note="단위: 고유 진입 흐름 수 · 사용자 수와 다릅니다">핵심 지표</SectionTitle>
            <DataMetadata data={data} dateLabel="진입 시작일 기준" />
            {kpis.entryCount === 0 && <Alert severity="info" sx={{ mb: 2 }}>수집이 완료된 기간이며 해당 조건의 진입 흐름은 0건입니다.</Alert>}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(5, minmax(0, 1fr))' }, gap: 1.5 }}>
                {metrics.map(([label, value, note], index) => <Metric key={label} label={label} value={value} note={note} accent={index === 4} />)}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>전체 진입 {formatCount(kpis.entryCount)}건 · 진입 대비 가입 완료율 {formatRate(kpis.entryToPurchaseRate)}</Typography>
            <Typography variant="caption" color="text.secondary">가입 완료는 구매 전환 실적입니다. 전체 신규 구독자나 현재 유효 구독자 수가 아니며, 자동 갱신은 포함하지 않습니다.</Typography>
        </Box>
        <Box>
            <SectionTitle note="진입점 이름을 클릭하면 단계별 전환율이 펼쳐집니다">진입점별 퍼널</SectionTitle>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small" aria-label="진입점별 멤버십 퍼널" sx={{ minWidth: 1000 }}>
                    <TableHead sx={{ bgcolor: '#f8fafb' }}><TableRow>
                        {['화면', '프로모션 ID', '노출', '클릭', '랜딩 진입', '가입 완료', '노출→가입', '진입→가입'].map((label, index) => <TableCell key={label} align={index < 2 ? 'left' : 'right'} sx={{ whiteSpace: 'nowrap', py: 1.75 }}>{label}</TableCell>)}
                    </TableRow></TableHead>
                    <TableBody>{data.sources.map(source => <SourceRow key={source.entrySource} source={source} />)}</TableBody>
                </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary">해당 없음: 수집하지 않는 단계 · —: 분모가 0이어서 계산할 수 없는 전환율</Typography>
        </Box>
        <Box>
            <SectionTitle note="선택한 진입점의 랜딩 진입 이후 가입 완료 기준">멤버십 혜택 안내 페이지</SectionTitle>
            <Paper sx={panelSx}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                            {[['랜딩 진입', formatCount(landing.pageViewCount)], ['가입 완료', formatCount(landing.purchaseCount)], ['랜딩 이후 전환율', formatRate(landing.pageViewToPurchaseRate)]].map(([label, value]) => <Box key={label}>
                                <Typography variant="body2" color="text.secondary">{label}</Typography><Typography sx={{ ...numberSx, fontSize: { xs: 22, md: 28 }, mt: 1 }}>{value}</Typography>
                            </Box>)}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>가입 완료 건의 플랜 비율</Typography>
                        <Distribution items={landing.plans} countKey="count" unit="건" label="가입 완료 플랜 비율" />
                    </Grid>
                </Grid>
            </Paper>
        </Box>
        <DailyTrend data={data} impressionSupported={impressionSupported} />
    </Stack>
}

export function OrderSegments({ data }) {
    const [tab, setTab] = useState('detailed')
    const { summary } = data
    return <Paper sx={panelSx}>
        <DataMetadata data={data} dateLabel="주문 결제일 기준" />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
            {[
                ['주문 수', `${formatCount(summary.orderCount)}건`], ['고유 주문자', `${formatCount(summary.orderUserCount)}명`],
                ['멤버십 가입 주문자', `${formatCount(summary.membershipUserCount)}명`], ['멤버십 가입 주문자 비율', formatRate(summary.membershipUserRate)],
            ].map(([label, value]) => <Box key={label}><Typography variant="body2" color="text.secondary">{label}</Typography><Typography sx={{ ...numberSx, fontSize: 24, mt: 0.5 }}>{value}</Typography></Box>)}
        </Box>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="주문자 구성 보기" sx={{ mb: 2 }}>
            <Tab id="membership-segment-detailed" aria-controls="membership-segment-panel" value="detailed" label="상세" />
            <Tab id="membership-segment-grouped" aria-controls="membership-segment-panel" value="grouped" label="통합" />
        </Tabs>
        {summary.orderUserCount === 0 && <Alert severity="info" sx={{ mb: 2 }}>조회 기간에 결제한 앱 주문자가 없습니다.</Alert>}
        <Box role="tabpanel" id="membership-segment-panel" aria-labelledby={`membership-segment-${tab}`}>
            <Distribution items={data[tab]} countKey="userCount" unit="명" label="주문자 멤버십 구성 비율" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>분류 확인율 {formatRate(summary.classificationCoverageRate)} · 미분류 {formatCount(summary.unknownUserCount)}명 포함</Typography>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.75 }}>서버 결제·멤버십 이력 기준 · 기간 내 신규/재가입 우선 분류</Typography>
        <Typography variant="caption" color="text.secondary">기간 전체의 주문자를 중복 제거합니다. 기말 현재 회원 구성이 아니며, 미분류도 비율의 분모에 포함합니다.</Typography>
    </Paper>
}
